import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import ApiCustom from "../../utils/ApiCustom";
import { formatDate } from "../../utils/helps";
import StatusDropdown from "../../components/admin/StatusDropdownButton";
import {
  BuildingOffice2Icon,
  UserIcon,
} from "@heroicons/react/24/outline";

const normalizeText = (value) => {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
};

const getMemberType = (company) => {
  const memberType = normalizeText(company?.memberType);

  if (
    ["individual", "personal", "person", "ca nhan", "ca_nhan"].includes(
      memberType,
    )
  ) {
    return "individual";
  }

  return "company";
};

const getMemberTypeLabel = (company) => {
  return getMemberType(company) === "individual" ? "Cá nhân" : "Doanh nghiệp";
};

const getMemberTypeBadgeClass = (company) => {
  return getMemberType(company) === "individual"
    ? "badge-warning"
    : "badge-info";
};

const NotPaidMembershipfeeModal = ({ onClose, membershipFeeId }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [memberTypeFilter, setMemberTypeFilter] = useState("all");

  useEffect(() => {
    if (!membershipFeeId) {
      setCompanies([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    ApiCustom.get(`/membership-fee-not-paid/${membershipFeeId}`)
      .then((res) => {
        const data = res.data?.data?.data?.companies || [];
        const companiesWithFee = data.map((company) => ({
          ...company,
          feeId: membershipFeeId,
        }));
        setCompanies(companiesWithFee);
      })
      .catch(() => {
        setCompanies([]);
      })
      .finally(() => setLoading(false));
  }, [membershipFeeId]);

  const handleShowInfo = async () => {
    if (!membershipFeeId) return;

    try {
      await ApiCustom.post(`/membership-fee-reminder/${membershipFeeId}`);
      toast.success("Đã gửi thông báo nhắc nhở đến các hội viên!");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Gửi thông báo thất bại!");
    }
  };

  const getCompanyResentUserName = (company) => {
    const user = company?.users?.find(
      (user) => user?.CompaniesHasUsers?.isUserRepresentOfCompany,
    );
    return user ? user?.userFullName : "";
  };

  const visibleCompanies = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    let list = companies;

    if (q) {
      list = list.filter((c) => {
        const name = (c.companyName || "").toLowerCase();
        const rep = (getCompanyResentUserName(c) || "").toLowerCase();
        return name.includes(q) || rep.includes(q);
      });
    }

    if (memberTypeFilter !== "all") {
      list = list.filter((c) => getMemberType(c) === memberTypeFilter);
    }

    return list;
  }, [companies, searchText, memberTypeFilter]);

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-base-100 rounded-lg shadow-xl w-11/12 xl:w-2/3 max-h-[90vh] flex flex-col">
        {/* Header sticky */}
        <div className="p-6 rounded-lg sticky top-0 bg-base-100 z-20 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Hội viên chưa đóng hội phí</h2>
            <button
              onClick={onClose}
              className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
            >
              X
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center flex-1 min-h-[100px]">
            <span className="text-gray-500">Đang tải dữ liệu...</span>
          </div>
        ) : companies?.length > 0 ? (
          <div className="overflow-y-auto flex-grow">
            {/* Toolbar tìm kiếm sticky, KHÔNG blur */}
            <div className="sticky top-0 z-10 bg-base-100 border-b">
              <div className="px-6 py-3 w-full flex flex-col lg:flex-row lg:items-center gap-3">
                <input
                  type="text"
                  className="input input-bordered w-full lg:max-w-sm"
                  placeholder="Tìm theo hội viên hoặc người đại diện…"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />

                <select
                  className="select select-bordered w-full lg:w-auto"
                  value={memberTypeFilter}
                  onChange={(e) => setMemberTypeFilter(e.target.value)}
                >
                  <option value="all">Tất cả loại hội viên</option>
                  <option value="company">Doanh nghiệp</option>
                  <option value="individual">Cá nhân</option>
                </select>
              </div>
            </div>

            {/* Bảng: cuộn ngang mobile */}
            <div
              className="w-full overflow-x-auto px-6 pb-6"
              style={{ WebkitOverflowScrolling: "touch" }}
            >
              <table className="table-auto w-full border-collapse border border-gray-300 my-1 min-w-[44rem]">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Tên hội viên
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Người đại diện
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-left">
                      Ngày cập nhật
                    </th>
                    <th className="border border-gray-300 px-4 py-2 text-center">
                      Trạng thái
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCompanies.length > 0 ? (
                    visibleCompanies.map((company) => (
                      <tr
                        key={company.id + "-" + (company.feeId || "")}
                        className="hover:bg-gray-50"
                      >
                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex flex-col items-start gap-1">
                            <span className="font-medium">
                              {company.companyName}
                            </span>

                            <span
                              className={`badge ${getMemberTypeBadgeClass(
                                company,
                              )} badge-outline gap-1`}
                            >
                              {getMemberType(company) === "individual" ? (
                                <UserIcon className="w-4 h-4" />
                              ) : (
                                <BuildingOffice2Icon className="w-4 h-4" />
                              )}
                              {getMemberTypeLabel(company)}
                            </span>
                          </div>
                        </td>

                        <td className="border border-gray-300 px-4 py-2">
                          {getCompanyResentUserName(company)}
                        </td>

                        <td className="border border-gray-300 px-4 py-2">
                          {formatDate(
                            company?.CompaniesMustPayMembershipFee?.updatedAt,
                          )}
                        </td>

                        <td className="border border-gray-300 px-4 py-2">
                          <div className="flex justify-center">
                            <StatusDropdown
                              disabled={true}
                              options={[
                                { value: false, label: "Chưa đóng" },
                                { value: true, label: "Đã đóng" },
                              ]}
                              value={false}
                              onChange={() => {}}
                              getStatusStyle={() =>
                                "bg-yellow-100 text-yellow-800"
                              }
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={4}
                        className="border border-gray-300 px-4 py-6 text-center text-gray-500"
                      >
                        Không có kết quả phù hợp
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <p className="text-center text-gray-500">
            Không có hội viên nào chưa đóng hội phí.
          </p>
        )}

        <div className="p-6 rounded-lg sticky bottom-0 bg-base-100 z-10">
          <div className="flex justify-end space-x-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Đóng
            </button>
            {companies?.length > 0 && (
              <button
                type="button"
                className="px-4 py-2 border btn-outline btn-info rounded-md shadow-sm text-sm font-medium text-white "
                onClick={handleShowInfo}
              >
                Gửi thông báo
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotPaidMembershipfeeModal;